import { ClipboardList, ImageIcon, PanelTop, Type } from 'lucide-react'
import { z } from 'zod'

type ToolSpec<TInput = any> = {
	description: string
	inputSchema: z.ZodType<TInput>
	icon: React.ComponentType<{ className?: string }>
	label: string
}

export const toolSpecs = {
	editImage: {
		icon: ImageIcon,
		label: 'Edit Image',
		description: 'Edit an existing image',
		inputSchema: z.object({
			imageEditRequest: z.string().describe('The edit request for the image'),
		}),
	},
	generateImages: {
		icon: ImageIcon,
		label: 'Generate Images',
		description: 'Generate (e.g. advertisement) images',
		inputSchema: z.object({
			imagePromptEditRequest: z.string().describe('Prompt for the image generation'),
		}),
	},
	generateAdCopy: {
		icon: Type,
		label: 'Generate Ad Copy',
		description: 'Generate or modify advertisement copy text',
		inputSchema: z.object({
			textPrompt: z.string().describe('The edit request for the copy prompt'),
		}),
	},
	getAdCopy: {
		icon: Type,
		label: 'Get Ad Copy',
		description: 'Get the currently visible ad copy text',
		inputSchema: z.object({
			getCopy: z.boolean().describe('Get the generated advertisement copy text'),
		}),
	},
	generateLandingPage: {
		icon: PanelTop,
		label: 'Generate Landing Page',
		description: 'Generate or modify landing page content',
		inputSchema: z.object({
			editRequest: z.string().describe('The edit request for the landing page generation'),
		}),
	},
	getLandingPageContent: {
		icon: PanelTop,
		label: 'Get Landing Page Content',
		description: 'Get the currently visible landing page content',
		inputSchema: z.object({
			getLandingPage: z.boolean().describe('Get the generated landing page content'),
		}),
	},
	modifySurvey: {
		icon: ClipboardList,
		label: 'Modify Questionnaire',
		description: 'Modify survey questions and structure',
		inputSchema: z.object({
			editRequest: z.string().describe('The edit request for the survey generation'),
		}),
	},
	getSurveyContent: {
		icon: ClipboardList,
		label: 'Get Questionnaire Content',
		description: 'Get the currently visible survey content',
		inputSchema: z.object({
			getSurvey: z.boolean().describe('Get the generated survey content'),
		}),
	},
	// NOTE: This tool is not currently used, enabling reasoning in the UI triggers
	// the creation of the profile before doing any generation.
	// generateCandidateProfile: {
	//   icon: <User2 className="size-3" strokeWidth={1} />,
	//   label: "Generating Candidate Profile",
	//   description: "Generate a candidate profile",
	//   inputSchema: z.object({
	//     profileData: z.string().describe("The data for the candidate profile"),
	//   }),
	// },
} as const satisfies Record<string, ToolSpec<any>>

// Derive tool names from toolSpecs
export const TOOL_NAMES = Object.keys(toolSpecs).reduce(
	(acc, key) => {
		return { ...acc, [key]: key }
	},
	{} as Record<keyof typeof toolSpecs, keyof typeof toolSpecs>,
)

// Extract server-side definitions (without handler, icon, label)
export const serverToolDefinitions = Object.entries(toolSpecs).reduce(
	(acc, [key, spec]) => {
		return {
			...acc,
			[key]: {
				description: spec.description,
				inputSchema: spec.inputSchema,
			},
		}
	},
	{} as Record<keyof typeof toolSpecs, { description: string; inputSchema: z.ZodType }>,
)

// Type helpers
export type ToolName = keyof typeof toolSpecs
